import { useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Modal, StatusPill, useToast } from '../../design-system';
import { formatMinutes, totalLogged } from './HoursMinutesInput';
import '../styles/Todos.css';
import { formatTaskNumber } from '../../lib/taskRef';

// Read-only "everything about this to-do" panel, opened by clicking a row's
// main area in the admin to-do list. Purely a viewer — edits still happen
// through the pencil (TodoEditModal) and time through TodoTimeModal.

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';


// Match the status wording the list's Select uses (todo => "Created").
const STATUS_LABEL = {
  todo: 'Created', 'in-progress': 'In Progress', review: 'Review',
  'waiting-client': 'Waiting on Client', completed: 'Completed', 'on-hold': 'On Hold',
};

function Row({ label, children }) {
  return (
    <div className="todo-detail-row">
      <span className="todo-detail-label">{label}</span>
      <span className="todo-detail-value">{children}</span>
    </div>
  );
}

function TodoDetailModal({ todo, onClose, onUpdated }) {
  const toast = useToast();
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  if (!todo) return null;

  const comments = todo.comments || [];

  // A to-do carries budgeted and logged time and gets delegated, so it needs a
  // place to discuss it — previously the only note was the single-line `remark`,
  // which is the CURRENT note, not a history.
  const postComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const res = await apiClient.post(`/api/todos/${todo._id}/comments`, { text: comment });
      onUpdated?.(res.data);
      setComment('');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (commentId) => {
    try {
      const res = await apiClient.delete(`/api/todos/${todo._id}/comments/${commentId}`);
      onUpdated?.(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove comment');
    }
  };

  const logged = totalLogged(todo);
  const alloc = todo.allocatedMinutes;
  const over = alloc != null && logged > alloc;
  const logs = todo.timeLogs || [];

  return (
    <Modal isOpen={!!todo} onClose={onClose} title="To-do details" size="md">
      <div className="todo-detail">
        <div className="todo-detail-heading">
          <span className="todo-detail-title">{todo.title}</span>
          <StatusPill status={todo.priority} />
          <StatusPill status={todo.status} label={STATUS_LABEL[todo.status]} />
        </div>

        <div className="todo-detail-grid">
          <Row label="Type">
            {todo.taskId ? (
              <>
                Task-linked · <strong>{formatTaskNumber(todo.taskId.taskNumber)}</strong>
                {todo.taskId.title ? ` — ${todo.taskId.title}` : ''}
              </>
            ) : (
              'General (not tied to a task)'
            )}
          </Row>
          <Row label="Priority">{(todo.priority || 'medium').replace(/^\w/, (c) => c.toUpperCase())}</Row>
          <Row label="Assignee">{todo.assignee?.name || 'Unassigned'}</Row>
          <Row label="Created by">{todo.createdBy?.name || '—'}</Row>
          <Row label="Created">{fmtDateTime(todo.createdAt)}</Row>
          <Row label="Due">{fmtDate(todo.dueDate)}</Row>
          {todo.completedAt && <Row label="Completed">{fmtDateTime(todo.completedAt)}</Row>}
          <Row label="Time">
            <span className={over ? 'todo-detail-over' : undefined}>
              {formatMinutes(logged)} logged
              {alloc != null ? ` / ${formatMinutes(alloc)} budgeted` : ''}
              {over ? ' · over budget' : ''}
            </span>
          </Row>
          <Row label="Remark">{todo.remark || '—'}</Row>
        </div>

        <div className="todo-detail-logs">
          <div className="todo-detail-subhead">Time log ({logs.length})</div>
          {logs.length === 0 ? (
            <div className="todo-detail-empty">No time logged yet.</div>
          ) : (
            <ul className="todo-detail-loglist">
              {logs.map((l, i) => (
                <li key={l._id || i}>
                  <span className="todo-detail-log-who">{l.employeeId?.name || 'Unknown'}</span>
                  <span className="todo-detail-log-mins">{formatMinutes(l.minutes)}</span>
                  <span className="todo-detail-log-when">{fmtDateTime(l.loggedAt)}</span>
                  {l.note && <span className="todo-detail-log-note">“{l.note}”</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="todo-detail-comments">
          <div className="todo-detail-subhead">Comments ({comments.length})</div>
          {comments.length === 0 ? (
            <div className="todo-detail-empty">No comments yet.</div>
          ) : (
            <ul className="todo-detail-commentlist">
              {[...comments].reverse().map((c) => (
                <li key={c._id}>
                  <div className="todo-comment-head">
                    <strong>{c.author || 'Someone'}</strong>
                    <span className="todo-comment-when">{fmtDateTime(c.createdAt)}</span>
                    <button
                      type="button"
                      className="todo-comment-remove"
                      onClick={() => removeComment(c._id)}
                      aria-label="Remove comment"
                    >
                      ×
                    </button>
                  </div>
                  <p className="todo-comment-text">{c.text}</p>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={postComment} className="todo-comment-form">
            <textarea
              className="ds-input"
              rows="2"
              placeholder="Add a comment…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button type="submit" variant="secondary" size="sm" loading={posting}>Post</Button>
          </form>
        </div>
      </div>
    </Modal>
  );
}

export default TodoDetailModal;
